import { useState } from 'react';
import { Plus, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { SocioRow } from './SocioRow';
import type { Socio } from '@/contabilidad/types';
import { useAlert } from '@/hooks/useAlert';

interface SociosEditorProps {
  initialSocios: Socio[];
  onSave: (socios: Socio[]) => Promise<Socio[]>;
  isSaving?: boolean;
  canEdit?: boolean;
}

export function SociosEditor({ initialSocios, onSave, isSaving = false, canEdit = true }: SociosEditorProps) {
  const { successAlert, errorAlert } = useAlert();
  const [socios, setSocios] = useState<Socio[]>(initialSocios);

  const total = socios.reduce((acc, s) => acc + s.porcentaje, 0);
  const totalRounded = Math.round(total * 10) / 10;
  const isValid = socios.length === 0 || totalRounded === 100;
  const hasNombresVacios = socios.some(s => !s.nombre.trim());

  const handleChange = (id: number, value: number) => {
    setSocios(prev => prev.map(s => s.id === id ? { ...s, porcentaje: value } : s));
  };

  const handleChangeNombre = (id: number, value: string) => {
    setSocios(prev => prev.map(s => s.id === id ? { ...s, nombre: value } : s));
  };

  const handleDelete = (id: number) => {
    setSocios(prev => prev.filter(s => s.id !== id));
  };

  const handleAdd = () => {
    const newSocio: Socio = {
      id: Date.now(),
      nombre: '',
      porcentaje: 0,
      isNew: true,
    };
    setSocios(prev => [...prev, newSocio]);
  };

  async function handleSave() {
    try {
      await onSave(socios);
      successAlert({ title: 'Socios guardados', message: 'La distribución de socios fue actualizada correctamente.' });
    } catch (err) {
      errorAlert({ title: 'Error al guardar socios', exception: err });
    }
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Distribución de Socios</p>
          {canEdit && (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleAdd}>
              <Plus className="size-3.5 mr-1" /> Agregar Socio
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Porcentaje total asignado</span>
            <span className={isValid ? 'text-blue-600 font-semibold' : 'text-rose-600 font-semibold'}>
              {totalRounded}%
            </span>
          </div>
          <Progress value={Math.min(totalRounded, 100)} className={isValid ? '[&>div]:bg-blue-500' : '[&>div]:bg-rose-500'} />
        </div>

        {!isValid && (
          <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            <AlertTriangle className="size-3.5 flex-shrink-0" />
            <span>El total debe ser exactamente 100%. Actualmente: <strong>{totalRounded}%</strong></span>
          </div>
        )}

        {isValid && socios.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <CheckCircle2 className="size-3.5 flex-shrink-0" />
            <span>Distribución válida. Podés guardar los cambios.</span>
          </div>
        )}

        <div>
          {socios.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">No hay socios. Agregá uno para empezar.</p>
          )}
          {socios.map(socio => (
            <SocioRow
              key={socio.id}
              socio={socio}
              onChangePorcentaje={handleChange}
              onChangeNombre={handleChangeNombre}
              onDelete={handleDelete}
              readOnly={!canEdit}
            />
          ))}
        </div>

        {canEdit && (
          <Button
            className="w-full"
            disabled={!isValid || hasNombresVacios || isSaving}
            onClick={handleSave}
          >
            {isSaving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
